import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "@/core/components/dv-forms/select";
import Input from "@/core/components/dv-forms/input";
import { useMutation } from "@tanstack/react-query";
import { UserService } from "@/core/services/userService";
import PaymentSimplex from "../../payments/simplex";
import { get } from "lodash";
import { BalanceService } from "@/core/services/balanceService";
import { useTranslation } from "react-i18next";
import QRCodeDisplay from "@/core/components/qr-code/QRCodeDisplay";
import { Warning } from "@phosphor-icons/react";

enum ENUM_DEPOSIT {
  EUR = 'eur',
  USDC = 'usdc',
  LTT = 'ltt'
}

const DEPOSIT_IN_OPTIONS = [
  { content: 'EUR', value: ENUM_DEPOSIT.EUR },
  { content: 'USDC', value: ENUM_DEPOSIT.USDC },
  { content: 'DVX', value: ENUM_DEPOSIT.LTT },
]

const MAX_IBAN_AMOUNT = 9999;

const DepositForm = ({ step, setStep, onClose }: { step: number, setStep: (step: number) => void, onClose: () => void }) => {
  const { t } = useTranslation();
  const { register, handleSubmit, control, formState: { errors }, watch, reset } = useForm({
    mode: 'onChange',
    defaultValues: {
      depositIn: DEPOSIT_IN_OPTIONS[0].value,
      amount: null,
    }
  });
  const amount = watch('amount');
  const depositIn = watch('depositIn');
  const [payment, setPayment] = useState<any>();
  const [depositAddress, setDepositAddress] = useState<string>('');

  const { mutate: getDepositAddress } = useMutation({
    mutationFn: (coin: string) => BalanceService.getDepositAddress(coin),
    onSuccess: (data) => {
      setDepositAddress(get(data, 'address', ''));
      setStep(1);
    },
  });

  const { mutate, isLoading } = useMutation(() => UserService.getSimplexQuote({ amount: Number(amount), currency: depositIn }), {
    onSuccess: (data) => {
      setPayment(data.response || data);
    }
  });

  const onSubmit = () => {
    if (Number(amount) <= MAX_IBAN_AMOUNT) {
      mutate();
    } else {
      setStep(step + 1);
    }
  }

  useEffect(() => {
    setDepositAddress('');
    if (depositIn !== DEPOSIT_IN_OPTIONS[0].value) {
      getDepositAddress(depositIn);
      setStep(1);
    } else {
      reset();
      setStep(0)
    }
  }, [depositIn]);

  useEffect(() => {
    if (step === 1) {
      setStep(0)
    }
  }, [amount])

  return (
    <>
      <div>
        <>
          <div className="general-modal__content" style={{ overflow: 'unset' }}>
            <div>
              <form className="dv-form">
                <Controller
                  name="depositIn"
                  control={control}
                  rules={{ required: t("form.required.deposit") }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label={t('common.deposit_in')}
                      placeholder="Select your deposit in"
                      options={DEPOSIT_IN_OPTIONS}
                    />
                  )}
                />
                {
                  depositIn === 'eur' && (
                    <Input
                      name="amount"
                      label={t('common.amount')}
                      placeholder="0"
                      register={register}
                      errors={errors}
                      note={t("error.deposit_iban")}
                      startAdornment={depositIn === 'eur' ? '€' : ''}
                      type="number"
                      rules={{ required: t("form.required.amount"), min: { value: 100, message: t("form.invalid.min_deposit") } }}
                    />
                  )
                }
              </form>
            </div>
          </div>
          <div className="general-modal__footer">
            {
              step === 1 ? (
                <div style={{ margin: "auto", display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {
                    depositIn === ENUM_DEPOSIT.EUR ? (
                      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                        <div style={{ fontWeight: 600, fontSize: '1rem' }}>{t('common.detail_transfer')}:</div>
                        <div style={{ fontSize: '0.95rem', lineHeight: '1.8' }}>
                          <div><b>IBAN:</b> FR76 1287 9000 0111 1499 4100 286</div>
                          <div><b>{t('common.beneficiary')}:</b> SAS DAVENSI - AUM</div>
                          <div><b>BIC:</b> DELUFR22XXX</div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '100%' }}>
                        <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: '8px' }}>{t('common.please_find_below')}:</div>
                        <QRCodeDisplay
                          address={depositAddress}
                          coinType={depositIn.toUpperCase()}
                          network={depositIn === ENUM_DEPOSIT.USDC ? 'ERC20' : 'BSC20'}
                        />
                        {/* Risk disclaimer */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px',
                          padding: '12px 16px',
                          backgroundColor: 'rgba(255, 193, 7, 0.1)',
                          border: '1px solid rgba(255, 193, 7, 0.3)',
                          borderRadius: '8px',
                          marginTop: '16px',
                          textAlign: 'left',
                          width: '100%'
                        }}>
                          <Warning size={20} weight="fill" style={{ color: '#FFC107', flexShrink: 0, marginTop: '2px' }} />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#FFC107' }}>
                              {t('common.deposit_risk_warning_title')}
                            </span>
                            <p style={{ fontSize: '0.85rem', lineHeight: 1.4, color: 'var(--text-secondary)', margin: 0 }}>
                              {t('common.deposit_risk_warning', {
                                coin: depositIn.toUpperCase(),
                                network: depositIn === ENUM_DEPOSIT.USDC ? 'Ethereum' : 'BSC'
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  }
                  <button className="cta-button cta-medium cta-red" onClick={onClose} style={{ minHeight: 45, width: 150, margin: '0 auto' }}>
                    <span>{t('common.close')}</span>
                  </button>
                </div>
              ) : (
                <>
                  <button className="cta-button cta-medium cta-white" disabled={Number(amount) <= MAX_IBAN_AMOUNT} onClick={handleSubmit(onSubmit)}>
                    <div className="general-modal__footer-icon">
                      <img src="/assets/images/icons/iban.png" alt="iban" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </div>
                    <span>{t("common.payment_via_iban")}</span>
                  </button>
                  <button 
                    className="cta-button cta-medium cta-blue"
                    disabled={Number(amount) > MAX_IBAN_AMOUNT || Number(amount) <= 0 || isLoading}
                    onClick={handleSubmit(onSubmit)}
                  >
                    <div className="general-modal__footer-icon">
                      <img src="/assets/images/icons/simplex.png" alt="bank" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </div>
                    <span>{t("common.payment_via_simplex")}</span>
                  </button>
                </>
              )
            }
          </div>
          {payment?.payment_id && <PaymentSimplex paymentId={payment.payment_id} partner={payment.partner} />}
        </>
      </div>
    </>
  )
}

export default DepositForm;

